/**
 * @fileoverview HTTP client with retry, timeout, and auth. Used by all packages for API calls.
 * Exports NetworkHelper (get, post, put) and API_STATUS. Uses document.location for Authorization header.
 */
import { uniqueId } from 'lodash';
import { getAuthorization } from './appConstants';
import { logger } from './logger';

const TIMEOUT_MS = 30000; // 30 seconds
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second

/** Creates an AbortController and a timeout that aborts after TIMEOUT_MS. */
const createAbortController = () => {
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
	return { controller, timeoutId };
};

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const retryWithBackoff = async (fn, retries = MAX_RETRIES) => {
	let lastError;
	for (let i = 0; i < retries; i++) {
		try {
			return await fn();
		} catch (error) {
			lastError = error;
			if (i < retries - 1) {
				const delay = INITIAL_RETRY_DELAY * 2 ** i; // Exponential backoff
				logger.warn(
					`Request failed, retrying in ${delay}ms (attempt ${i + 1}/${retries})`,
					error
				);
				await sleep(delay);
			}
		}
	}
	throw lastError;
};

/**
 * Wraps a fetch error with method, url, and status for logging and rejection.
 * @param {Error} error - Original error
 * @param {string} method - HTTP method (GET, POST, PUT)
 * @param {string} url - Request URL
 * @returns {Error} New error with originalError, method, url, status
 */
const enrichError = (error, method, url) => {
	const enriched = new Error(`HTTP ${method} request to ${url} failed`);
	enriched.originalError = error;
	enriched.method = method;
	enriched.url = url;
	enriched.status = error.status || null;
	return enriched;
};

/**
 * HTTP helper with Authorization header, 30s timeout, and exponential backoff (3 retries).
 * API_STATUS: NOT_STARTED, INITIALIZED, SUCCESS, FAILURE for UI state.
 */
export const NetworkHelper = {
	/**
	 * GET request. Returns parsed JSON. Rejects with enriched error on !res.ok or network failure.
	 * @param {string} url - Full URL
	 * @returns {Promise<object>} Response body as object
	 */
	get: url => {
		return retryWithBackoff(async () => {
			const { controller, timeoutId } = createAbortController();
			try {
				const res = await fetch(url, {
					method: 'GET',
					headers: {
						Authorization: getAuthorization()
					},
					signal: controller.signal
				});
				clearTimeout(timeoutId);
				if (!res.ok) {
					const error = new Error(`HTTP ${res.status}: ${res.statusText}`);
					error.status = res.status;
					throw error;
				}
				return await res.json();
			} catch (error) {
				clearTimeout(timeoutId);
				if (error.name === 'AbortError') {
					const timeoutError = enrichError(new Error('Request timeout'), 'GET', url);
					logger.error('GET request timeout', timeoutError);
					throw timeoutError;
				}
				const enrichedError = enrichError(error, 'GET', url);
				logger.error('GET request failed', enrichedError);
				throw enrichedError;
			}
		});
	},

	/**
	 * POST request. Sends JSON body. Rejects with enriched error on !res.ok or network failure.
	 * @param {string} url - Full URL
	 * @param {object} payload - Body (will be JSON.stringify'd)
	 * @param {object} [headers={}] - Extra headers merged with Content-Type and Authorization
	 * @returns {Promise<object>} Response body as object
	 */
	post: (url, payload, headers = {}) => {
		return new Promise((resolve, reject) => {
			const { controller, timeoutId } = createAbortController();
			fetch(url, {
				method: 'POST',
				body: JSON.stringify(payload),
				headers: {
					'Content-Type': 'application/json',
					Authorization: getAuthorization(),
					...headers
				},
				signal: controller.signal
			})
				.then(res => {
					clearTimeout(timeoutId);
					if (!res.ok) {
						const error = new Error(`HTTP ${res.status}: ${res.statusText}`);
						error.status = res.status;
						throw error;
					}
					return res.json();
				})
				.then(data => {
					resolve(data);
				})
				.catch(error => {
					clearTimeout(timeoutId);
					if (error.name === 'AbortError') {
						const timeoutError = enrichError(new Error('Request timeout'), 'POST', url);
						logger.error('POST request timeout', timeoutError);
						reject(timeoutError);
					} else {
						const enrichedError = enrichError(error, 'POST', url);
						logger.error('POST request failed', enrichedError);
						reject(enrichedError);
					}
				});
		});
	},

	/**
	 * PUT request. Sends JSON body. Rejects with enriched error on !res.ok or network failure.
	 * @param {string} url - Full URL
	 * @param {object} [payload={}] - Body (will be JSON.stringify'd)
	 * @returns {Promise<object>} Response body as object
	 */
	put: (url, payload = {}) => {
		return new Promise((resolve, reject) => {
			const { controller, timeoutId } = createAbortController();
			fetch(url, {
				method: 'PUT',
				body: JSON.stringify(payload),
				headers: {
					'Content-Type': 'application/json',
					Authorization: getAuthorization()
				},
				signal: controller.signal
			})
				.then(res => {
					clearTimeout(timeoutId);
					if (!res.ok) {
						const error = new Error(`HTTP ${res.status}: ${res.statusText}`);
						error.status = res.status;
						throw error;
					}
					return res.json();
				})
				.then(data => {
					resolve(data);
				})
				.catch(error => {
					clearTimeout(timeoutId);
					if (error.name === 'AbortError') {
						const timeoutError = enrichError(new Error('Request timeout'), 'PUT', url);
						logger.error('PUT request timeout', timeoutError);
						reject(timeoutError);
					} else {
						const enrichedError = enrichError(error, 'PUT', url);
						logger.error('PUT request failed', enrichedError);
						reject(enrichedError);
					}
				});
		});
	},

	API_STATUS: {
		NOT_STARTED: null,
		INITIALIZED: 0,
		SUCCESS: 1,
		FAILURE: -1
	}
};
